import { GAME_CONFIG } from '../config';
import { MODELS } from '../config/llm.config';
import { LLMAdapter, LLMAdapterFactory } from '../adapters/llm';
import { PromptBuilder } from '../builders/prompt';
import { SceneData, GameState } from '../types';
import { Logger } from '../utils/Logger';

/**
 * LLM服务类，负责与LLM API交互
 */
export class LLMService {
    private static instance: LLMService;
    private modelId: string;
    private adapter: LLMAdapter;
    private promptBuilder: PromptBuilder;
    private maxRetries: number;
    private logger: Logger;

    /**
     * 构造函数
     * @param modelId 模型ID
     */
    private constructor(modelId: string) {
        this.modelId = modelId;
        this.adapter = LLMAdapterFactory.createAdapter(modelId);
        this.promptBuilder = new PromptBuilder();
        this.maxRetries = GAME_CONFIG.maxRetries;
        this.logger = Logger.getInstance();
    }

    /**
     * 获取单例实例
     * @param modelId 模型ID
     * @returns LLMService实例
     */
    static getInstance(modelId: string = GAME_CONFIG.defaultModel): LLMService {
        if (!LLMService.instance || LLMService.instance.modelId !== modelId) {
            LLMService.instance = new LLMService(modelId);
        }
        return LLMService.instance;
    }

    /**
     * 设置新的模型
     * @param modelId 新的模型ID
     * @param forceRecreate 是否强制重新创建适配器
     */
    setModel(modelId: string, forceRecreate: boolean = false): void {
        if (this.modelId !== modelId || forceRecreate) {
            this.modelId = modelId;
            this.adapter = LLMAdapterFactory.createAdapter(modelId);
            
            this.logger.info('MODEL_CHANGE', `模型已切换为: ${modelId}`, {
                previousModel: this.modelId,
                newModel: modelId,
                forceRecreate
            });
        }
    }

    /**
     * 生成对话内容
     * @param gameState 游戏状态
     * @param playerChoice 玩家选择
     * @returns 生成的场景数据
     */
    async generateDialogue(gameState: GameState, playerChoice: string): Promise<SceneData> {
        const prompt = this.promptBuilder.buildPrompt(gameState, playerChoice);
        return this.callAPIWithRetry(prompt);
    }

    private async callAPIWithRetry(prompt: string): Promise<SceneData> {
        let lastError: Error | null = null;
        
        for (let i = 0; i < this.maxRetries; i++) {
            try {
                const result = await this.callAPI(prompt);
                if (result) {
                    return result;
                }
            } catch (error) {
                lastError = error as Error;
                this.logger.error('API_CALL_RETRY', `API调用失败，尝试重试 (${i + 1}/${this.maxRetries})`, {
                    error: error instanceof Error ? error.message : String(error),
                    retryCount: i + 1
                });
                
                // 在重试之前等待一段时间
                await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 1000));
            }
        }
        
        return this.createErrorScene(lastError);
    }

    private async callAPI(prompt: string): Promise<SceneData | null> {
        try {
            // 准备请求
            const { endpoint, headers, body } = await this.adapter.prepareRequest(prompt);
            
            // 发送请求
            const response = await fetch(endpoint, {
                method: 'POST',
                headers,
                body: JSON.stringify(body)
            });
            
            // 检查响应状态
            if (!response.ok) {
                throw await this.adapter.handleErrorResponse(response);
            }
            
            // 处理响应
            return await this.adapter.processResponse(response);
        } catch (error) {
            this.logger.error('API_CALL_ERROR', 'API调用失败', {
                error: error instanceof Error ? error.message : String(error)
            });
            throw error;
        }
    }

    private createErrorScene(error: Error | null): SceneData {
        const errorMessage = error ? error.message : '未知错误';
        
        this.logger.error('SCENE_ERROR', '创建错误场景', {
            error: errorMessage
        });
        
        return {
            scene_text: "发生了一些技术故障...",
            npc_dialogue: `错误信息: ${errorMessage}`,
            choices: [
                {
                    text: "重试",
                    next_scene: "retry",
                    requirements: null
                }
            ]
        };
    }

    private validateSceneData(data: any): data is SceneData {
        return (
            typeof data === 'object' &&
            typeof data.scene_text === 'string' &&
            Array.isArray(data.choices) &&
            data.choices.every((choice: any) =>
                typeof choice === 'object' &&
                typeof choice.text === 'string' &&
                typeof choice.next_scene === 'string' &&
                (choice.requirements === null || typeof choice.requirements === 'string')
            )
        );
    }

    /**
     * 根据模型ID获取模型配置
     * @param modelId 模型ID
     * @returns 模型配置或undefined
     */
    private getModelConfig(modelId: string) {
        return MODELS.find(m => m.id === modelId);
    }
}